<template>
  <div class="tournament-registration t-text" data-qe-id="tournament-registration-modal">
    <gd-modal
      v-if="isRegistrationOverlayShown && !failedReason"
      @closeBG="closeRegistrationModal"
      @closeESC="closeRegistrationModal"
      @close="closeRegistrationModal"
      :class="isProcessingRegistration ? 'modal-processing' : ''"
    >
      <modal-app s>
        <modal-layout data-qe-id="tournament-registration-state">
          <c-loading-indicator class="modal-spinner" :show="isProcessingRegistration"></c-loading-indicator>
          <modal-title v-if="rejoin">{{ $t('tRegistration.main.title_rejoin') }}</modal-title>
          <modal-title v-else> {{ $t('tRegistration.main.title') }} </modal-title>
          <modal-content>
            <h3>{{ tournament.name }}</h3>
            <c-tournament-info
              :tournament-ui="tournamentUi"
              gameName
              totalWin
              addedPrize
              entryFee
              restartFee
              competitionDate
              lobbyTime
              registrationDate
              tournamentMode
              :rejoin="rejoin"
            />
            <p v-if="rejoin" data-qe-id="rejoin-text">
              {{ $t('tRegistration.main.rejoin_text', { fee: tournamentUi.metaData.restartFee }) }}
            </p>
            <div class="checkbox-group">
              <input type="checkbox" v-model="acceptanceBox" id="acceptance-box" />
              <!-- TODO: removed data-qe-id="link-tc" -->
              <label
                v-html="md($t('tRegistration.main.acceptance'))"
                @click="clickHandlerTerms"
                for="acceptance-box"
                data-qe-id="checkbox-accept-tc"
              >
              </label>
              <c-terms
                :show="showTerms"
                :is-masters="tournamentUi.isMasters"
                :game-type="tournamentUi.gametype"
                @close-terms="showTerms = false"
              />
            </div>
            <p class="error-text" v-if="failedReasonInline" data-qe-id="inline-error">{{ failedReasonInline }}</p>
          </modal-content>
          <modal-actions class="join-modal-actions">
            <t-button v-if="!rejoin" @click="onRegister" yellow l data-qe-id="join-button">
              {{ $t('tRegistration.main.button_join', { fee: tournamentUi.metaData.entryFee }) }}
            </t-button>
            <t-button v-else @click="onRegister" yellow l data-qe-id="rejoin-button">
              {{ $t('tRegistration.main.button_join', { fee: tournamentUi.metaData.restartFee }) }}
            </t-button>
          </modal-actions>
        </modal-layout>
      </modal-app>
    </gd-modal>
    <c-error-modal @close="isRegistrationOverlayShown = false" v-else-if="isRegistrationOverlayShown">
      <div @click="clickHandler" v-html="md($t('tRegistration.error.' + failedReason))"></div>
    </c-error-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'
import CTournamentInfo from '@/components/CTournamentInfo.vue'
import CTerms from '@/components/terms/CTerms.vue'
import CErrorModal from '@/components/CErrorModal.vue'
import { Tournament, TournamentUi } from '../model/Tournament'
import { State } from '../store/state'
import { JoinState } from '../store/modules/join'
import { marked } from 'marked'
import { ERROR_CODES } from '../model/ErrorCodes'
import { track } from '@/lib/track'
import { jslog } from '@gd/jslog'

const emit = defineEmits(['registration-complete'])
const router = useRouter()
const route = useRoute()
const store = useStore()
const { t } = useI18n()

// activate the rejoin flag ( different texts )
const rejoin = ref(false)
const acceptanceBox = ref(false)

const isRegistrationOverlayShown = ref(false)
const isProcessingRegistration = ref(false)
// JoinState.result.reason
const failedReason = ref('')
const failedReasonInline = ref('')

const showTerms = ref(false)

const tournament = computed((): Tournament => {
  return store.state.tournament
})
function clickHandlerTerms(e: Event) {
  const event: any = e
  if (event.target && event.target.tagName === 'A' && event.target.href) {
    if (/#terms/.test(event.target.href)) {
      showTerms.value = true
      e.preventDefault()
    }
  }
}
function clickHandler(e: Event) {
  const event: any = e
  if (event.target && event.target.tagName === 'A' && event.target.href) {
    if (/#customerservice/.test(event.target.href)) {
      const win: any = window.open('/gd/support/contact.xhtml', 'customer')
      win.focus()
    }
  }
  e.preventDefault()
}

const tournamentUi = computed((): TournamentUi => {
  return store.getters.tournamentUi
})

/**
 * Public method to show the registration overlay
 */
function show(conf: { rejoin: boolean }) {
  rejoin.value = conf.rejoin
  isProcessingRegistration.value = false
  failedReason.value = ''
  failedReasonInline.value = ''
  isRegistrationOverlayShown.value = true
}

function closeRegistrationModal() {
  if (!isProcessingRegistration.value) {
    isRegistrationOverlayShown.value = false
  }
}
const balance = computed((): number => {
  return parseFloat((store.state as State).balance.balance)
})
function onRegister() {
  if (!acceptanceBox.value) {
    failedReasonInline.value = t('tRegistration.main.error_acceptance') as string
    return
  }

  if (
    balance.value <
    parseFloat(!rejoin.value ? tournament.value.metaData.entryFee : tournament.value.metaData.restartFee)
  ) {
    failedReasonInline.value = t('tRegistration.main.error_notEnoughMoney') as string
    return
  }

  track({
    path: route.fullPath,
    id: 'tournament-registration',
    data: rejoin.value ? 'rejoin' : 'join',
  })

  isProcessingRegistration.value = true
  store
    .dispatch('join/registerForTournament', (store.state as State).tournament.tournamentId)
    .then(() => {
      if (store.state.join.result.success) {
        store.commit('joinTournament', (store.state as State).tournament.tournamentId)
        isRegistrationOverlayShown.value = false
        isProcessingRegistration.value = false
        emit('registration-complete')
      } else {
        const reason = (store.state.join as JoinState).result.reason as GD_tournaments.FailReason
        if (reason === 'tooMuchMoney') {
          router.replace('/extern/payout')
          return
        }

        isProcessingRegistration.value = false
        failedReason.value = reason
        jslog.send({
          name: 'tournament-registration',
          data: { failedReason: failedReason.value },
        })
      }
      // trigger reload tournament
    })
    .catch((err) => {
      // handle "fatal" errors
      if (err && err.errorCode === 401) {
        router.replace('/extern/login')
      } else {
        jslog.send({
          name: 'tournament-registration',
          data: {
            code: (err && err.errorCode) || ERROR_CODES.UNKNOWN,
            zoneId: (store.state as State).zone.zoneId,
          },
        })
      }
      failedReason.value = 'unknown'
      isProcessingRegistration.value = false
    })
}

function md(text: string): string {
  return marked(text)
}

defineExpose({
  show,
})
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import '~@npm/@gd/common-styles/dist/color-vars';
@import '~@npm/@gd/common-styles/lib/checkbox';
.tournament-registration {
  user-select: none;
  text-align: left;
  h3,
  .modal-title {
    text-align: center;
  }
  .modal-elements-hidden {
    // visibility: hidden;
  }
  .modal-content {
    position: relative;
    min-width: 20em;
    min-height: 5em;
    width: 100%;
  }
  .gd-modal-component-body {
    overflow: visible;
  }
  .checkbox-group {
    .checkbox();
    padding-top: 0.2em;
    display: flex;

    label {
      p {
        padding: 0;
        margin: 0;
      }
    }

    .terms {
      display: inline;
    }
  }
  .error-text {
    color: red;
  }
  .join-modal-actions {
    align-items: center;
  }
  .tournament-info {
    .name {
      text-align: right;
      flex: 30 0;
    }
    .value {
    }
  }
}
</style>
